import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { LARGO_MINIMO_CONTRASENA } from '@copathy/shared';
import { ErrorApi } from '../lib/api';
import { useActivarCuenta } from '../lib/usuarios';
import { Boton } from '../componentes/Boton';
import { Campo } from '../componentes/Campo';
import { Marca } from '../componentes/Marca';
import { PanelPresentacion } from '../componentes/PanelPresentacion';
import { Candado, Check, Ojo, OjoTachado } from '../componentes/iconos';

/**
 * Pantalla del enlace de invitación: la persona elige su contraseña.
 *
 * ── Por qué acá NO se consume el token al abrir ──
 * `VerificarCorreo` sí lo hace, porque su único efecto es marcar el correo como
 * verificado. Acá hace falta que la persona escriba algo, así que el token se
 * consume al enviar. Eso también evita que un previsualizador de enlaces
 * —WhatsApp, el antivirus del correo corporativo— queme la invitación con solo
 * pasar por la URL.
 */
export function ActivarCuenta() {
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';

  const [contrasena, setContrasena] = useState('');
  const [repetida, setRepetida] = useState('');
  const [verClave, setVerClave] = useState(false);
  const [campos, setCampos] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [listo, setListo] = useState<{ nombre: string; empresaNombre: string } | null>(null);

  const activar = useActivarCuenta();

  const noCoinciden = repetida.length > 0 && contrasena !== repetida;
  const puedeEnviar =
    token.length > 0 && contrasena.length >= LARGO_MINIMO_CONTRASENA && contrasena === repetida;

  async function enviar() {
    setError(null);
    setCampos({});
    try {
      const r = await activar.mutateAsync({ token, contrasena });
      setListo({ nombre: r.nombre, empresaNombre: r.empresaNombre });
    } catch (e) {
      if (e instanceof ErrorApi) {
        if (e.campos) setCampos(e.campos);
        else setError(e.message);
      } else {
        setError('No pudimos activar la cuenta. Intenta de nuevo.');
      }
    }
  }

  return (
    <div className="flex min-h-full flex-col lg:flex-row">
      <PanelPresentacion />

      <main className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden">
            <Marca tamano="md" />
          </div>

          {!token && (
            <Aviso titulo="El enlace está incompleto">
              Ábrelo directamente desde el correo que te llegó, sin copiarlo a mano.
            </Aviso>
          )}

          {listo ? (
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-green-50 text-green-700">
                <Check size={28} />
              </div>
              <h1 className="text-2xl font-bold text-marino-800">Listo, {listo.nombre}</h1>
              <p className="mt-2 text-marino-500">
                Tu cuenta de <strong className="text-marino-700">{listo.empresaNombre}</strong> ya
                está activa. Entra con tu correo y la contraseña que acabas de elegir.
              </p>
              <Link
                to="/entrar"
                className="mt-6 inline-flex items-center justify-center rounded-2xl bg-naranja-500 px-5 py-3
                  font-semibold text-marino-900 transition hover:bg-naranja-400"
              >
                Ingresar
              </Link>
            </div>
          ) : (
            token && (
              <>
                <h1 className="text-2xl font-bold text-marino-800">Elige tu contraseña</h1>
                <p className="mt-2 text-sm text-marino-500">
                  COPATHY te creó una cuenta. La contraseña la eliges tú: nadie más la ve.
                </p>

                {error && (
                  <div
                    role="alert"
                    className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
                  >
                    {error}
                  </div>
                )}

                <form
                  className="mt-6 space-y-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void enviar();
                  }}
                >
                  <Campo
                    etiqueta="Contraseña"
                    type={verClave ? 'text' : 'password'}
                    icono={<Candado size={18} />}
                    value={contrasena}
                    onChange={(e) => setContrasena(e.target.value)}
                    error={campos.contrasena}
                    autoFocus
                    derecha={
                      <button
                        type="button"
                        onClick={() => setVerClave((v) => !v)}
                        aria-label={verClave ? 'Ocultar contraseña' : 'Ver contraseña'}
                        className="rounded-full p-2 text-marino-400 transition hover:text-marino-700"
                      >
                        {verClave ? <OjoTachado size={18} /> : <Ojo size={18} />}
                      </button>
                    }
                  />

                  <Campo
                    etiqueta="Ingrese nuevamente la contraseña"
                    type={verClave ? 'text' : 'password'}
                    icono={<Candado size={18} />}
                    value={repetida}
                    onChange={(e) => setRepetida(e.target.value)}
                    error={noCoinciden ? 'Las dos no coinciden.' : undefined}
                  />

                  {/*
                    La política se dice ANTES de escribir, no como error después.
                    Un requisito que solo aparece al fallar obliga a adivinar.
                  */}
                  <p className="rounded-xl border border-marino-100 bg-marino-50/60 px-4 py-3 text-xs text-marino-500">
                    Mínimo {LARGO_MINIMO_CONTRASENA} caracteres.
                  </p>

                  <Boton
                    type="submit"
                    className="w-full"
                    cargando={activar.isPending}
                    disabled={!puedeEnviar}
                  >
                    Activar mi cuenta
                  </Boton>
                </form>
              </>
            )
          )}
        </div>
      </main>
    </div>
  );
}

function Aviso({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4">
      <h1 className="font-semibold text-amber-900">{titulo}</h1>
      <p className="mt-1 text-sm text-amber-800">{children}</p>
    </div>
  );
}
